import { DEFAULT } from 'customer-data-objects/view/ViewTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import H5 from 'UIComponents/elements/headings/H5';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import { PopoverFooter, PopoverHeader, PopoverBody } from 'foundations-components/transitional/components/Popover';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { CREATE_VIEW_MODAL } from '../../constants/modalTypes';
import { useDispatch, useSelector } from '../../context/Redux';
import { transformTempViewIntoSavedViewFormat } from '../../formatters/views';
import { useUserId } from '../../hooks/auth';
import { useActiveView } from '../../hooks/useActiveView';
import { useFetchViewPermissionsById } from '../../hooks/useFetchViewPermissionsById';
import { useUpdateView } from '../../hooks/useUpdateView';
import { openModalAction } from '../../redux/actions/overlay';
import { resetView } from '../../redux/actions/tempViewState';
import { tempViewStateSelector } from '../../redux/selectors/tempViewState';
import { SAVED_VIEW_LOCATIONS, trackSavedViewFiltersColumnsUpdated } from '../../tracking/tracker';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SaveViewButton = ({
  viewId
}) => {
  const {
    loading: originalViewLoading,
    error,
    data: {
      view: originalView
    } = {
      view: {}
    }
  } = useActiveView();
  const {
    loading: viewPermissionsLoading
  } = useFetchViewPermissionsById(viewId);
  const viewFromState = useSelector(tempViewStateSelector)[viewId];
  const tempView = useMemo(() => !originalViewLoading && viewFromState ? // some view fields are omitted when put into temp state but we want them
  // to be there to use if we save the view (ex: name, ownerId, type)
  Object.assign({}, originalView, viewFromState) : null, [originalView, originalViewLoading, viewFromState]);
  const currentUserId = useUserId();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  // if error, stop loading state so we can show disabled state
  const loading = useMemo(() => {
    if (error) {
      return false;
    }
    return originalViewLoading || viewPermissionsLoading || !tempView;
  }, [originalViewLoading, viewPermissionsLoading, error, tempView]);
  const handleSaveViewSuccess = useCallback(({
    view
  }) => {
    // if a new view is created, then will go through the
    // create view modal which has its own alerts so only
    // need to account for the edit flow here

    // reset the view so that the "Reset filters" button goes away
    dispatch(resetView({
      viewId,
      view
    }));
    setIsOpen(false);
    trackSavedViewFiltersColumnsUpdated({
      id: viewId,
      filters: view.filters,
      columns: (view.columns || []).map(column => column.name)
    });
  }, [viewId, dispatch]);
  const [updateView, {
    loading: updateViewLoading
  }] = useUpdateView({
    onCompleted: handleSaveViewSuccess
  });
  const handleSaveView = useCallback(() => {
    const formattedView = transformTempViewIntoSavedViewFormat(tempView);
    updateView({
      variables: {
        view: formattedView
      }
    }).catch(e => {
      console.error(e);
    });
  }, [updateView, tempView]);
  const handleResetView = useCallback(() => {
    dispatch(resetView({
      viewId,
      view: originalView
    }));
    setIsOpen(false);
  }, [dispatch, originalView, viewId]);
  const handleSaveAsNewView = useCallback(() => {
    const transformedView = transformTempViewIntoSavedViewFormat(tempView);
    dispatch(openModalAction({
      modalType: CREATE_VIEW_MODAL,
      modalData: {
        triggeredFrom: SAVED_VIEW_LOCATIONS.SAVED_VIEW_BUTTON,
        view: transformedView
      }
    }));
  }, [dispatch, tempView]);
  const isReadOnlyView = useMemo(() => originalViewLoading ||
  // if view isn't loaded yet, then don't allow user to make changes
  originalView.type === DEFAULT || String(originalView.ownerId) !== String(currentUserId), [originalViewLoading, originalView, currentUserId]);
  const popoverHeader = useMemo(() => isReadOnlyView ? /*#__PURE__*/_jsx(H5, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "saveView.readOnlyView.header"
    })
  }) : /*#__PURE__*/_jsx(H5, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "saveView.editableView.header"
    })
  }), [isReadOnlyView]);
  const popoverBody = useMemo(() => /*#__PURE__*/_jsxs(UIFlex, {
    wrap: "wrap",
    direction: "column",
    children: [isReadOnlyView ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "saveView.readOnlyView.message"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "saveView.editableView.message"
    }), /*#__PURE__*/_jsxs(UIFlex, {
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(UILoadingButton, {
        use: "tertiary",
        "data-test-id": "save-view-save-button",
        "data-selenium-test": "save-view-save-button",
        loading: updateViewLoading,
        disabled: isReadOnlyView,
        onClick: handleSaveView,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "saveView.saveButton"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": "save-view-reset-button",
        "data-test-id": "save-view-reset-button",
        use: "tertiary-light",
        onClick: handleResetView,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "saveView.resetButton"
        })
      })]
    })]
  }), [isReadOnlyView, updateViewLoading, handleSaveView, handleResetView]);
  const popoverFooter = useMemo(() => /*#__PURE__*/_jsx(UIButton, {
    "data-selenium-test": "save-view-save-as-new-button",
    "data-test-id": "save-view-save-as-new-button",
    use: "link",
    onClick: handleSaveAsNewView,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "saveView.saveAsNewButton"
    })
  }), [handleSaveAsNewView]);
  const getPopoverContent = useCallback(() => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PopoverHeader, {
      children: popoverHeader
    }), /*#__PURE__*/_jsx(PopoverBody, {
      children: popoverBody
    }), /*#__PURE__*/_jsx(PopoverFooter, {
      children: popoverFooter
    })]
  }), [popoverHeader, popoverBody, popoverFooter]);
  return /*#__PURE__*/_jsx(View, {
    as: "span",
    marginInlineEnd: "300",
    children: /*#__PURE__*/_jsx(Popover, {
      "data-component-name": "UIPopover",
      open: isOpen,
      Content: getPopoverContent,
      inlineSize: "325px",
      onOpenChange: e => {
        setIsOpen(e.target.value);
      },
      children: /*#__PURE__*/_jsxs(UILoadingButton, {
        use: "tertiary-light",
        "data-selenium-test": "save-view-button",
        "data-test-id": loading ? 'save-view-button-loading' : 'save-view-button',
        disabled: Boolean(error),
        loading: loading,
        failed: true // does not show the result success icon
        ,
        onClick: () => setIsOpen(prevState => !prevState),
        size: 'sm',
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "saveEditableView",
          size: "small"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "saveView.tasksTableSaveViewButton"
        })]
      })
    })
  });
};
export default SaveViewButton;